import { Button, Card, Form, Input, Select, Switch } from "antd";
import { Country } from "country-state-city";
import { useEffect, useState } from "react";
import { useUserStore } from "../../stores/userStore";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

export const MyPageForm = ({ form, disabled = true, onSave, setDisabled }) => {
  const { userInfo, loadUserInfo } = useUserStore();
  const [userInfoDisabled, setUserInfoDisabled] = useState(true);
  const [phoneValue, setPhoneValue] = useState("");

  useEffect(() => {
    if (userInfo?.user_info) {
      form.setFieldsValue({
        first_name: userInfo.user_info.first_name,
        last_name: userInfo.user_info.last_name,
        email: userInfo.user_info.email,
        phone: userInfo.user_info.phone,
        country: userInfo.user_info.country,
        state_province: userInfo.user_info.state_province,
        city: userInfo.user_info.city,
        postal_code: userInfo.user_info.postal_code,
        street_address: userInfo.user_info.street_address,
        address_line: userInfo.user_info.address_line,
        marketing_agreement: userInfo.user_info.marketing_agreement,
      });

      setPhoneValue(userInfo.user_info.phone || "");
    }
  }, [userInfo, form]);

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div>기본 정보</div>
            <div className="flex gap-2 items-center">
              {userInfoDisabled ? (
                <Button
                  type="primary"
                  htmlType="button"
                  size="small"
                  className="!bg-[#171717]"
                  onClick={() => setUserInfoDisabled(false)}
                >
                  수정
                </Button>
              ) : (
                <>
                  <Button
                    htmlType="button"
                    size="small"
                    className="!border-[#171717] !text-[#171717] hover:!border-[#171717] hover:!text-[#171717]"
                    onClick={() => {
                      form.resetFields();
                      setPhoneValue(userInfo?.user_info?.phone || "");
                      setUserInfoDisabled(true);
                      loadUserInfo();
                    }}
                  >
                    취소
                  </Button>
                  <Button
                    type="primary"
                    htmlType="button"
                    size="small"
                    className="!bg-[#171717]"
                    onClick={async () => {
                      const success = await onSave();
                      if (success) {
                        setPhoneValue(form.getFieldValue("phone"));
                        setUserInfoDisabled(true);
                      }
                    }}
                  >
                    완료
                  </Button>
                </>
              )}
            </div>
          </div>
        }
        className="w-full max-w-[600px] border border-[#d9d9d9] rounded-[10px] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)]"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={userInfo?.user_info || {}}
          disabled={userInfoDisabled}
          requiredMark={false}
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-0">
            <Form.Item
              label="이메일"
              name="email"
              rules={[{ required: true, message: "이메일을 입력해주세요." }]}
              className="col-span-2 mb-4"
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="성"
              name="last_name"
              rules={[{ required: true, message: "성을 입력해주세요." }]}
              className="mb-4"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="이름"
              name="first_name"
              rules={[{ required: true, message: "이름을 입력해주세요." }]}
              className="mb-4"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="전화번호"
              name="phone"
              rules={[
                { required: true, message: "전화번호를 입력해주세요." },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    return isValidPhoneNumber(value)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("유효한 전화번호가 아닙니다.")
                        );
                  },
                },
              ]}
              className="col-span-2 mb-4"
            >
              <div className={userInfoDisabled ? "phone-input-disabled" : ""}>
                <PhoneInput
                  international
                  placeholder="전화번호를 입력해주세요"
                  disabled={userInfoDisabled}
                  value={phoneValue}
                  onChange={(value) => {
                    setPhoneValue(value);
                    form.setFieldsValue({ phone: value });
                    form.validateFields(["phone"]);
                  }}
                />
              </div>
            </Form.Item>

            <Form.Item
              name="country"
              label="국가"
              rules={[{ required: true, message: "국가를 선택해주세요" }]}
              className="col-span-2 mb-4"
            >
              <Select
                placeholder="국가를 선택해주세요"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {Country.getAllCountries().map((country) => (
                  <Select.Option key={country.isoCode} value={country.isoCode}>
                    {country.name} ({country.isoCode})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="시/도"
              name="state_province"
              rules={[{ required: true, message: "시/도를 입력해주세요." }]}
              className="mb-4"
            >
              <Input placeholder="시/도를 입력해주세요 (예: 서울특별시, 경기도)" />
            </Form.Item>
            <Form.Item
              label="시/군/구"
              name="city"
              rules={[{ required: true, message: "시/군/구를 입력해주세요." }]}
              className="mb-4"
            >
              <Input placeholder="시/군/구를 입력해주세요 (예: 마포구, 강남구)" />
            </Form.Item>

            <Form.Item
              label="도로명 주소"
              name="street_address"
              rules={[
                { required: true, message: "도로명 주소를 입력해주세요." },
              ]}
              className="mb-4"
            >
              <Input placeholder="도로명 주소를 입력해주세요 (예: 마포대로 123)" />
            </Form.Item>

            <Form.Item
              label="우편번호"
              name="postal_code"
              rules={[{ required: true, message: "우편번호를 입력해주세요." }]}
              className="mb-4"
            >
              <Input placeholder="우편번호를 입력해주세요" />
            </Form.Item>

            <Form.Item
              label="상세주소"
              name="address_line"
              rules={[{ required: true, message: "상세주소를 입력해주세요." }]}
              className="col-span-2 mb-4"
            >
              <Input placeholder="상세주소를 입력해주세요 (예: 래미안아파트 101동 1504호)" />
            </Form.Item>

            <Form.Item
              label="마케팅 동의"
              name="marketing_agreement"
              valuePropName="checked"
              className="col-span-2 mb-0"
            >
              <Switch
                checkedChildren="동의함"
                unCheckedChildren="동의안함"
                defaultChecked={form.getFieldValue("marketing_agreement")}
              />
            </Form.Item>
          </div>
        </Form>
      </Card>
    </div>
  );
};
